import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Gift, 
  Copy, 
  Share2,
  Twitter,
  MessageCircle,
  CheckCircle,
  Users,
  ArrowRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function ReferralWidget({ userEmail, referralCode, onCodeGenerated }) {
  const [email, setEmail] = useState(userEmail || '');
  const [code, setCode] = useState(referralCode || '');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(!!referralCode);

  useEffect(() => {
    // Check if user already has a referral code in localStorage
    const savedCode = localStorage.getItem('referral_code');
    const savedEmail = localStorage.getItem('referral_email');
    if (savedCode) {
      setCode(savedCode);
      setEmail(savedEmail || '');
      setSubmitted(true);
      loadReferralStats(savedCode);
    }
    
    // Check for incoming referral code in URL
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get('ref');
    if (refCode) {
      localStorage.setItem('incoming_ref', refCode);
      trackReferralVisit(refCode);
    }
  }, []);

  const trackReferralVisit = async (refCode) => {
    try {
      await axios.post(`${API_URL}/api/referral/track?ref_code=${refCode}`);
    } catch (error) {
      console.error('Error tracking referral:', error);
    }
  };

  const loadReferralStats = async (refCode) => {
    // For pre-launch, we just show basic stats from localStorage
    const referrals = JSON.parse(localStorage.getItem(`referrals_${refCode}`) || '[]');
    setStats({ referral_count: referrals.length });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }

    setLoading(true);
    try {
      // First, check if this email already has a referral code (from VIP or account)
      const checkResponse = await axios.get(`${API_URL}/api/referral/check-email/${encodeURIComponent(email)}`);
      
      let generatedCode;
      if (checkResponse.data.has_code) {
        // Use existing code from VIP application or account
        generatedCode = checkResponse.data.referral_code;
        
        if (checkResponse.data.source === 'vip_application') {
          toast.success('Welcome back! Using your VIP application referral code.');
        } else {
          toast.success('Welcome back! Using your existing referral code.');
        }
      } else {
        // Generate a new referral code
        generatedCode = btoa(email).substring(0, 8).replace(/[^a-zA-Z0-9]/g, 'x');
        toast.success('Your referral link is ready!');
      }
      
      // Save to localStorage
      localStorage.setItem('referral_code', generatedCode);
      localStorage.setItem('referral_email', email);
      
      setCode(generatedCode);
      setSubmitted(true);
      
      if (onCodeGenerated) {
        onCodeGenerated(generatedCode);
      }
    } catch (error) {
      // Fallback to generating a code locally if API fails
      const generatedCode = btoa(email).substring(0, 8).replace(/[^a-zA-Z0-9]/g, 'x');
      localStorage.setItem('referral_code', generatedCode);
      localStorage.setItem('referral_email', email);
      setCode(generatedCode);
      setSubmitted(true);
      toast.success('Your referral link is ready!');
    } finally {
      setLoading(false);
    }
  };

  const referralLink = code ? `https://theailedger.ai?ref=${code}` : '';

  const copyLink = () => {
    if (referralLink) {
      navigator.clipboard.writeText(referralLink);
      toast.success('Link copied!');
    }
  };

  const shareOnTwitter = () => {
    const text = encodeURIComponent(`🚀 I just signed up for early access to theailedger.ai - The Marketplace for AI Products!\n\nJoin the waitlist with my link and we both get VIP perks:`);
    const url = encodeURIComponent(referralLink);
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank');
  };

  const shareOnWhatsApp = () => {
    const text = encodeURIComponent(`Check out theailedger.ai - The Marketplace for AI Products! 🚀\n\nJoin the waitlist with my link: ${referralLink}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  if (!submitted) {
    return (
      <div className="bg-gradient-to-r from-white/20 to-[#00F0FF]/20 border border-white/10 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <Gift className="w-6 h-6 text-[#00F0FF]" />
          <h3 className="font-bold text-lg">Get Your Referral Link</h3>
        </div>
        <p className="text-neutral-300 text-sm mb-4">
          Refer friends and earn rewards! Get discounts, credits, and exclusive badges.
        </p>
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
          <Input
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex-1 bg-black/50 border-white/10"
            required
          />
          <Button 
            type="submit"
            disabled={loading}
            className="bg-[#00F0FF] hover:bg-[#FFC000] text-black font-bold"
          >
            {loading ? 'Creating...' : 'Get Link'}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-white/20 to-[#00F0FF]/20 border border-white/10 rounded-2xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Gift className="w-6 h-6 text-[#00F0FF]" />
          <h3 className="font-bold text-lg">Your Referral Link</h3>
        </div>
        {stats && (
          <div className="flex items-center gap-2 bg-black/30 rounded-full px-3 py-1">
            <Users className="w-4 h-4 text-[#00F0FF]" />
            <span className="text-sm font-semibold">{stats.referral_count} referred</span>
          </div>
        )}
      </div>

      {/* Referral Link */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="flex-1 bg-black/50 border border-white/10 rounded-lg px-4 py-2 text-sm truncate">
          {referralLink}
        </div>
        <Button 
          onClick={copyLink}
          size="sm"
          className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black font-semibold"
        >
          <Copy className="w-4 h-4 mr-1" />
          Copy
        </Button>
      </div>

      {/* Share Buttons */}
      <div className="flex gap-2">
        <Button 
          onClick={shareOnTwitter}
          size="sm"
          variant="outline" 
          className="flex-1 border-[#1DA1F2] text-[#1DA1F2] hover:bg-[#1DA1F2]/10"
        >
          <Twitter className="w-4 h-4 mr-1" />
          Tweet
        </Button>
        <Button 
          onClick={shareOnWhatsApp}
          size="sm"
          variant="outline"
          className="flex-1 border-[#25D366] text-[#25D366] hover:bg-[#25D366]/10"
        >
          <MessageCircle className="w-4 h-4 mr-1" />
          WhatsApp
        </Button>
      </div>

      {/* Rewards Info */}
      <div className="mt-4 pt-4 border-t border-white/10">
        <p className="text-xs text-neutral-400 mb-2">Earn rewards for your first purchase:</p>
        <div className="grid grid-cols-3 gap-2 text-center text-xs">
          <div className="bg-black/30 rounded-lg p-2">
            <div className="text-[#00FF94] font-bold">1 referral</div>
            <div className="text-neutral-400">10% off</div>
          </div>
          <div className="bg-black/30 rounded-lg p-2">
            <div className="text-[#00FF94] font-bold">3 referrals</div>
            <div className="text-neutral-400">$5 off</div>
          </div>
          <div className="bg-black/30 rounded-lg p-2">
            <div className="text-[#00FF94] font-bold">10 referrals</div>
            <div className="text-neutral-400">$20 off</div>
          </div>
        </div>
        <p className="text-[10px] text-neutral-500 mt-2 text-center">*All discounts apply to your first purchase</p>
      </div>
    </div>
  );
}

export default ReferralWidget;
